/**
 * Firebase Configuration
 * Centralized Firebase initialization - single source of truth
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getAuth, type Auth } from 'firebase/auth'
import { getStorage, type FirebaseStorage } from 'firebase/storage'
import type { FirebaseConfig } from '../types'

// Validate environment variables
const validateEnvVar = (key: string, value: string | undefined): string => {
  if (!value) {
    console.error(`[Firebase Config] Missing env var: ${key}`)
    console.error(`[Firebase Config] All VITE_ env vars:`, import.meta.env)
    throw new Error(`Missing required environment variable: ${key}`)
  }
  return value
}

// Debug: Log env vars at load time
console.log('[Firebase Config] Loading environment variables...')
console.log('[Firebase Config] VITE_FIREBASE_API_KEY:', import.meta.env.VITE_FIREBASE_API_KEY ? 'SET (hidden)' : 'NOT SET')
console.log('[Firebase Config] VITE_FIREBASE_PROJECT_ID:', import.meta.env.VITE_FIREBASE_PROJECT_ID)

// Firebase configuration from environment variables
const firebaseConfig: FirebaseConfig = {
  apiKey: validateEnvVar('VITE_FIREBASE_API_KEY', import.meta.env.VITE_FIREBASE_API_KEY),
  authDomain: validateEnvVar('VITE_FIREBASE_AUTH_DOMAIN', import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: validateEnvVar('VITE_FIREBASE_PROJECT_ID', import.meta.env.VITE_FIREBASE_PROJECT_ID),
  storageBucket: validateEnvVar(
    'VITE_FIREBASE_STORAGE_BUCKET',
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET
  ),
  messagingSenderId: validateEnvVar(
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID
  ),
  appId: validateEnvVar('VITE_FIREBASE_APP_ID', import.meta.env.VITE_FIREBASE_APP_ID),
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

// Initialize Firebase App
export const app: FirebaseApp = initializeApp(firebaseConfig)

// Initialize Firebase Services
export const db: Firestore = getFirestore(app)
export const auth: Auth = getAuth(app)
export const storage: FirebaseStorage = getStorage(app)

// Export config for debugging (without sensitive data in production)
export const getConfig = (): Partial<FirebaseConfig> => {
  if (import.meta.env.VITE_APP_ENV === 'production') {
    return {
      projectId: firebaseConfig.projectId,
      authDomain: firebaseConfig.authDomain,
    }
  }
  return firebaseConfig
}

// Collection names as constants to prevent typos
export const COLLECTIONS = {
  REPORTS: 'reports',
  ADMINS: 'admins',
  USERS: 'users',
} as const

export type CollectionName = (typeof COLLECTIONS)[keyof typeof COLLECTIONS]
